import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'

//Providers
import { GlobalController } from 'src/app/controllers/global.controller'

//Controllers
import { StorageController } from './../controllers/storage.controller'

import { serverAddresses } from './server.provider'
import { Router } from '@angular/router'
import { features, tenantConfig } from '@env/tenant'
import { TranslocoService } from '@jsverse/transloco'
import { firstValueFrom } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class AuthProvider {
  public token: any = ''
  public user: any = null
  public isAuthenticated = false
  server = serverAddresses.api

  constructor(
    public http: HttpClient,
    private readonly globalCtrl: GlobalController,
    private readonly storageCtrl: StorageController,
    private readonly router: Router,
    private readonly transloco: TranslocoService
  ) { }

  async initAuth() {
    return Promise.all([this.getUser(), this.getToken()])
  }

  checkAuthentication() {
    return new Promise((resolve, reject) => {
      this.storageCtrl.get('token').then(
        token => {
          this.token = token
          resolve(token)
        },
        error => reject(error)
      )
    })
  }

  setToken(token) {
    this.token = 'Bearer ' + token
    this.storageCtrl.set('token', this.token)
  }

  async setUser(user) {
    this.user = user
    await this.storageCtrl.set('user', this.user)
  }

  async setLoginInfo(accessToken: string, user: UserLogin, onComplete?: () => void, redirectTo?: string) {
    this.setToken(accessToken)
    await this.setUser(user)
    // Seul l'employe a une interface dans sa langue (page des heures). Les
    // gestionnaires restent dans la langue du tenant, comme apres un
    // rafraichissement : appliquer la langue du profil ici affichait le
    // francais chez Canada jusqu'au prochain rechargement.
    const language = user.role === 'employee' ? this.getUserLanguage() : tenantConfig.locale.default_language
    // L'initialiseur ne precharge que la langue du tenant : sans attendre le
    // catalogue de la langue du profil, le translate() du toast de connexion
    // rendrait la cle brute sur un cache froid.
    try {
      await firstValueFrom(this.transloco.load(language))
    } catch (err) {
      console.error(err)
    }
    this.globalCtrl.setLanguage(language)

    if (onComplete) {
      try {
        onComplete()
      } catch (err) {
        console.log('err', err)
      }
    }

    const navigateTo = redirectTo
      ? this.router.navigate([redirectTo])
      : this.router.navigateByUrl(this.getNavigateUrlByRole(user.role))

    navigateTo.then(() => {
      this.globalCtrl.toggleMenu()
      this.globalCtrl.showConfToast(this.transloco.translate('APP.LOGIN_GREETING', { email: user.email }))
    })
  }

  private getUser() {
    return new Promise((resolve, reject) => {
      if (this.user) {
        resolve(this.user)
      } else {
        this.storageCtrl
          .get('user')
          .then(user => {
            if (user) {
              this.user = user
              resolve(this.user)
            } else {
              resolve(null)
            }
          })
          .catch(err => {
            console.error(err)
            resolve(null)
          })
      }
    })
  }

  private getToken() {
    return new Promise((resolve, reject) => {
      if (this.token) {
        resolve(this.token)
      } else {
        this.storageCtrl
          .get('token')
          .then(token => {
            if (token) {
              this.token = token
              resolve(this.token)
            } else {
              resolve(null)
            }
          })
          .catch(err => {
            console.error(err)
            resolve(null)
          })
      }
    })
  }

  login(credentials) {
    return new Promise<LoginResponse>((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http
        .post<LoginResponse>(this.server + '/login', credentials, {
          headers: headers,
          withCredentials: true,
        })
        .subscribe(
          res => {
            resolve(res)
          },
          err => {
            console.error(err)
            reject(err)
          }
        )
    })
  }

  logout() {
    this.token = null
    this.user = null
    this.setToken(null)
    this.setUser(null)
    this.storageCtrl.clear()
    // L'employe a pu basculer de langue : la page de connexion revient a celle du tenant.
    this.globalCtrl.setLanguage(tenantConfig.locale.default_language)
    const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
    // logout in the API deletes jwt cookies.
    this.http.get(this.server + '/logout', { headers: headers, withCredentials: true }).subscribe()
  }

  getUserRole() {
    return this.user && this.user.role ? this.user.role : 'employee'
  }

  getUserId() {
    return this.user && this.user._id ? this.user._id : null
  }

  getUserFirstName() {
    return this.user && this.user.firstName ? this.user.firstName : null
  }

  getUserFullName() {
    return this.user && this.user.firstName && this.user.lastName
      ? this.user.firstName + ' ' + this.user.lastName
      : null
  }

  getUserLanguage() {
    // Fall back to the tenant default language (canada_env → 'en', quebec_env → 'fr')
    // when the user has no stored preference. Used by the time entry page on init.
    // Unilingual tenants ignore the stored preference: a canada_env employee whose
    // record still says 'fr' must not land on French pages with no way back.
    if (features['i18n.language_switch'] !== true) return tenantConfig.locale.default_language
    return this.user && this.user.language ? this.user.language : tenantConfig.locale.default_language
  }

  changePassword(newPassword, token) {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      headers = headers.append('Authorization', 'Bearer ' + token)
      this.http
        .put(this.server + '/forgot-password', newPassword, {
          headers: headers,
          withCredentials: true,
        })
        .subscribe(
          (res: any) => {
            resolve(res)
          },
          err => {
            console.error(err)
            reject(err)
          }
        )
    })
  }

  async sendPasswordResetEmailOrSMS(credential: EmailOrPhone) {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http.post(this.server + '/forgot-password', credential, { headers: headers }).subscribe(
        (res: any) => {
          resolve(res)
        },
        err => {
          console.error(err)
          reject(err)
        }
      )
    })
  }

  loginWithMagicLink(magicToken) {
    return new Promise<LoginResponse>((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http
        .post<LoginResponse>(this.server + '/login-magic-link', { token: magicToken }, { headers: headers })
        .subscribe(
          res => {
            resolve(res)
          },
          err => {
            console.error(err)
            reject(err)
          }
        )
    })
  }

  getNavigateUrlByRole(role) {
    switch (role) {
      case 'admin':
        return '/overview?1'
      case 'employee':
        return '/time-entries'
      case 'supervisor':
        return '/dashboard-supervisor'
    }
  }
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: UserLogin
}

interface UserLogin {
  _id: string
  role: string
  firstName: string
  lastName: string
  language: string
  email: string
}

type EmailOrPhone = { email: string } | { phone: string }
